import React, { ReactElement, useMemo } from 'react'
import Markdown from 'markdown-to-jsx'
import katex from 'katex'
import { useRouter } from 'next/router'
import styles from './AiMarkdown.module.less'

// Renders Eupheus-authored markdown for creators: markdown via
// markdown-to-jsx, LaTeX via KaTeX ($…$ / $$…$$), tables, and links
// (editor deep links navigate in-app; external links open a new tab).
// Port of Website-Frontend's AiMarkdown (keep fixes in sync).

const MATH_HINT =
    /\\[a-zA-Z]+|[\^_]|[a-zA-Z]\s*[=+\-*/×÷<>≤≥]\s*[a-zA-Z0-9(]|\d\s*[=+\-*/×÷<>≤≥]\s*[a-zA-Z\\(]|^\s*[a-zA-Z]{1,2}\s*$|^\s*\(?[a-zA-Z0-9]+\s*[=+\-*/×÷]/

const renderMath = (expression: string, display: boolean): string => {
    try {
        // `\$` inside math is exactly what KaTeX wants for a dollar sign —
        // pass it through untouched.
        return katex.renderToString(expression, {
            displayMode: display,
            throwOnError: false,
            errorColor: '#cc0000',
            output: 'html',
            strict: 'ignore',
            trust: false,
        })
    } catch {
        return display ? `$$${expression}$$` : `$${expression}$`
    }
}

const prepareMath = (
    source: string
): { text: string; blocks: Record<string, string> } => {
    const blocks: Record<string, string> = {}
    let counter = 0
    const token = (html: string): string => {
        const id = `AIMATHTOKEN${counter++}`
        blocks[id] = html
        return `<aimath data-id="${id}"></aimath>`
    }
    let text = source.replace(/\$\$([\s\S]+?)\$\$/g, (_match, expr: string) =>
        token(renderMath(expr.trim(), true))
    )
    text = text.replace(
        /(^|[^\\$])\$([^$\n]+?)\$(?![\d])/g,
        (match, lead: string, expr: string) => {
            if (/^[\d,.\s]+$/.test(expr)) return match
            if (!MATH_HINT.test(expr) && !/[\\{}]/.test(expr)) return match
            return `${lead}${token(renderMath(expr, false))}`
        }
    )
    text = text.replace(/\\\$/g, '$')
    return { text, blocks }
}

const isInternalHref = (href: string): boolean => {
    if (href.startsWith('/')) return true
    if (typeof window === 'undefined') return false
    try {
        return (
            new URL(href, window.location.origin).origin ===
            window.location.origin
        )
    } catch {
        return false
    }
}

export const AiMarkdown: React.FC<{
    children: string
    onNavigate?: () => void
}> = ({ children, onNavigate }): ReactElement => {
    const router = useRouter()
    const { text, blocks } = useMemo(() => prepareMath(children), [children])
    return (
        <div className={styles.aiMarkdown}>
            <Markdown
                options={{
                    forceBlock: true,
                    overrides: {
                        aimath: {
                            component: ({
                                'data-id': id,
                            }: {
                                'data-id': string
                            }) => (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: blocks[id] ?? '',
                                    }}
                                />
                            ),
                        },
                        a: {
                            component: ({
                                href,
                                children: linkChildren,
                                ...rest
                            }: {
                                href?: string
                                children?: React.ReactNode
                            }) => {
                                const internal = !!href && isInternalHref(href)
                                return (
                                    <a
                                        {...rest}
                                        href={href}
                                        target={internal ? undefined : '_blank'}
                                        rel={
                                            internal
                                                ? undefined
                                                : 'noopener noreferrer'
                                        }
                                        onClick={(event) => {
                                            if (!internal || !href) return
                                            if (event.metaKey || event.ctrlKey)
                                                return
                                            event.preventDefault()
                                            onNavigate?.()
                                            const url = new URL(
                                                href,
                                                window.location.origin
                                            )
                                            void router.push(
                                                url.pathname +
                                                    url.search +
                                                    url.hash
                                            )
                                        }}
                                    >
                                        {linkChildren}
                                    </a>
                                )
                            },
                        },
                        table: {
                            component: ({
                                children: tableChildren,
                            }: {
                                children?: React.ReactNode
                            }) => (
                                <div className={styles.tableWrap}>
                                    <table>{tableChildren}</table>
                                </div>
                            ),
                        },
                    },
                }}
            >
                {text}
            </Markdown>
        </div>
    )
}
